
import { Component, Prop, Vue } from 'vue-property-decorator';
import { BottomWrapper } from '../BottomWrapper.vue';
import { Bridge } from '@/common/bridge';
import { namespace } from 'vuex-class';
import { isEmailValid, isPhoneValid } from '@/common/utils';
import waiting from '@/components/loading/waiting';
import { LoginResult, PleasedGuestUserInfo, PleasedUserInfo } from '@/common/types';
import * as rule from '@/common/lib/form-valid';
import LogPageEventMixin, { PAGE_TRACKING, PAGE_EVENTS } from '@/mixins/logPageEvent';
import { mixins } from 'vue-class-component';
import trackClick, { BTN_EVTS } from '@/directives/trackClick';
import { analytics } from '@/common/logs/EventLogger';

const userModule = namespace('user');

interface LoginForm {
  username: string;
  password: string;
}

@Component({
  components: {
    BottomWrapper,
  },
  directives: {
    waiting,
    trackClick,
  },
})
export default class LoginGuest extends mixins(LogPageEventMixin) {
  @Prop({ default: false }) readonly isGuestAllowed!: boolean;

  @userModule.State('country') country!: string;
  @userModule.State('guestUser') guestUser!: PleasedGuestUserInfo;
  @userModule.State('usernameFieldType') usernameFieldType!: 'phone' | 'email';

  @userModule.Action('sportyLoginSuccess') sportyLoginSuccess!: (payload: LoginResult) => Promise<void>;

  readonly [PAGE_TRACKING] = PAGE_EVENTS.CONNECT_REQUEST_LOGIN;
  readonly BTN_EVTS = BTN_EVTS;

  loading = false;
  loginErrorFromApi = false;

  form = {
    username: '',
    password: '',
  };

  error = {
    username: '',
    password: '',
  };

  get buttons () {
    return [{
      name: this.t('page.login.button.login', 'Login'),
      id: '',
      isActive: true,
    }];
  };

  get showGuestLoginOption () {
    return this.isGuestAllowed && !this.guestUser.filledDetails;
  }

  get userFieldType () {
    return this.usernameFieldType === 'email' ? {
      type: 'email',
      name: this.$t('global.email'),
      inputType: 'email',
    } : {
      type: 'phone',
      name: this.t('page.login.mobile.placeholder', 'Mobile no'),
      inputType: 'tel',
    };
  }

  get loginFormSuite (): rule.ValidationSuite<LoginForm> {
    return {
      username: [
        rule.c(rule.required, '{uFieldName} is required'),
        rule.c(
          (val: string, _: any, ctx: any) =>
            ctx.uFieldType === 'phone' ? isPhoneValid(val) : isEmailValid(val),
          'Please enter a valid {uFieldName}'
        ),
      ],
      password: [rule.c(rule.required, 'Password is required')],
    };
  };

  protected cleanPopListener: CallableFunction | null = null;

  created () {
    document.title = 'Login Guest';
    this.cleanPopListener = Bridge.onLoginFromPopup(async (data) => {
      this.loading = true;
      await this.onLoginSuccess(data);
    });
  }

  async submit () {
    this.loginErrorFromApi = false;
    this.loading = true;
    const { valid, errors } = await rule.validateForm(this.form, this.loginFormSuite, { uFieldType: this.userFieldType.type, uFieldName: this.userFieldType.name });
    this.error = Object.assign({}, this.error, errors);
    analytics.trackClick({
      name: this.BTN_EVTS.CONNECT_REQUEST_LOGIN_CLICKED,
      metadata: { valid, errors },
    });

    if (!valid) {
      this.loading = false;
      return;
    }
    const data = await Bridge.login({ username: this.form.username, password: this.form.password });
    console.log('login data', data);
    await this.onLoginSuccess(data);
    this.loading = false;
  }

  destroyed () {
    this.cleanPopListener?.();
  }

  async onLoginSuccess (data: LoginResult) {
    console.log('onLoginSuccess data', data);
    if (data.bizCode === 10000) {
      await this.sportyLoginSuccess(data).catch(() => {});
      this.$emit('login');
    } else {
      this.loginErrorFromApi = true;
    }
  }
}
